import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import api from '../../services/api';
import WorkerLayout from '../../layouts/WorkerLayout';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import { STATES, getDistricts } from '../../data/indiaLocations';

const STEPS = ['Personal', 'Address', 'Work & Skills', 'Preferences', 'Documents'];

export default function ProfileCreate() {
  const navigate = useNavigate();
  const [step, setStep]         = useState(0);
  const [loading, setLoading]   = useState(false);
  const [skills, setSkills]     = useState([]);
  const [langs, setLangs]       = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [workerId, setWorkerId] = useState(null);
  const [selectedSkills, setSelectedSkills]     = useState([]);
  const [selectedLangs, setSelectedLangs]       = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [profileExists, setProfileExists] = useState(false);
  const [selectedState, setSelectedState]   = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [locationId, setLocationId]         = useState(null);

  const { register, handleSubmit, formState: { errors }, getValues, watch, setError, reset } = useForm();

  useEffect(() => {
    Promise.all([
      api.get('/lookup/skills'),
      api.get('/lookup/languages'),
      api.get('/lookup/work-categories'),
      api.get('/worker/profile').catch(() => null),
    ]).then(([s, l, c, profileRes]) => {
      setSkills(s.data.data);
      setLangs(l.data.data);
      setCategories(c.data.data);

      if (profileRes?.data?.data) {
        const p = profileRes.data.data;
        setProfileExists(true);
        setWorkerId(p.id);
        setStep(1);
        if (p.state)    setSelectedState(p.state);
        if (p.district) setSelectedDistrict(p.district);
        if (p.location_id) setLocationId(p.location_id);
        reset({
          address_line: p.address_line || '',
          pincode: p.pincode || '',
          experience_years: p.experience_years ?? '',
          travel_radius_km: p.travel_radius_km ?? '',
          daily_wage_min: p.daily_wage_min || '',
          daily_wage_max: p.daily_wage_max || '',
          mobile_alt: p.mobile_alt || '',
          availability_status: p.availability_status || 'available',
          preferred_work_type: p.preferences?.preferred_work_type || 'daily_wage',
          open_to_relocation: p.preferences?.open_to_relocation || false,
          shift_preference: p.preferences?.shift_preference || 'any',
        });
        if (p.skills?.length) {
          setSelectedSkills(p.skills.map((s) => s.skill_id));
        }
        if (p.languages?.length) {
          setSelectedLangs(p.languages.map((l) => l.language_id));
        }
        if (p.preferredCategories?.length) {
          setSelectedCategories(p.preferredCategories.map((c) => c.category_id));
        }
      }
    }).catch(() => {});
  }, []);

  async function onPersonalSubmit(data) {
    if (step === 0) {
      setLoading(true);
      try {
        const res = await api.post('/worker/profile', data);
        setWorkerId(res.data.data.workerId);
        setStep(1);
        toast.success('Basic profile saved!');
      } catch (err) {
        const fieldErrors = err?.response?.data?.errors;
        if (fieldErrors?.length) {
          fieldErrors.forEach(({ field, message }) => {
            setError(field, { type: 'server', message });
          });
        }
      } finally { setLoading(false); }
    }
  }

  async function saveSkills() {
    if (!selectedSkills.length) return setStep(3);
    try {
      await api.put('/worker/profile/skills', {
        skills: selectedSkills.map((id) => ({ skill_id: id, proficiency: 'beginner', is_primary: false })),
      });
      setStep(3);
    } catch {}
  }

  async function savePreferences() {
    try {
      await api.put('/worker/profile/preferences', {
        categories: selectedCategories,
        languages: selectedLangs,
        preferred_work_type: getValues('preferred_work_type'),
        open_to_relocation: getValues('open_to_relocation') || false,
        shift_preference: getValues('shift_preference') || 'any',
      });
      setStep(4);
    } catch {}
  }

  function toggleItem(arr, setArr, id) {
    setArr(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  const stepContent = [
    // Step 0: Personal
    <form onSubmit={handleSubmit(onPersonalSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        <Input label="Full Name" name="full_name" placeholder="As on Aadhaar" required
          {...register('full_name', { required: 'Name is required' })} error={errors.full_name?.message} />
        <Input label="Father / Husband Name" name="guardian_name" placeholder="Guardian's full name"
          {...register('guardian_name')} />
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Gender <span className="text-red-500">*</span></label>
          <div className="grid grid-cols-3 gap-2">
            {['male', 'female', 'other'].map((g) => (
              <label key={g} className="flex items-center gap-2 p-2.5 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input type="radio" value={g} {...register('gender', { required: 'Gender is required' })} />
                <span className="text-sm capitalize">{g === 'male' ? 'Male' : g === 'female' ? 'Female' : 'Other'}</span>
              </label>
            ))}
          </div>
          {errors.gender && <p className="text-xs text-red-600">{errors.gender.message}</p>}
        </div>
        <Input label="Date of Birth" name="dob" type="date" required
          {...register('dob', { required: 'DOB is required' })} error={errors.dob?.message} />
        <Input label="Aadhaar Last 4 Digits" name="aadhaar_last4" inputMode="numeric" placeholder="XXXX"
          hint="We only store the last 4 digits for privacy" maxLength={4}
          {...register('aadhaar_last4', { pattern: { value: /^\d{4}$/, message: 'Enter 4 digits' } })}
          error={errors.aadhaar_last4?.message} />
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Education Level</label>
          <select className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 focus:outline-none"
            {...register('education_level')}>
            <option value="illiterate">Not Educated / निरक्षर</option>
            <option value="primary">Primary / प्राथमिक</option>
            <option value="middle">Middle / मध्य</option>
            <option value="secondary">Secondary / माध्यमिक (10th)</option>
            <option value="higher_secondary">Higher Secondary / उच्च माध्यमिक (12th)</option>
            <option value="graduate">Graduate / स्नातक</option>
          </select>
        </div>
        <Input label="Emergency Contact Name" name="emergency_contact_name" placeholder="Name"
          {...register('emergency_contact_name')} />
        <Input label="Emergency Contact Mobile" name="emergency_contact_mobile" type="tel" inputMode="numeric" placeholder="10-digit number"
          {...register('emergency_contact_mobile')} />
      </div>
      <Button type="submit" fullWidth loading={loading}>Save & Continue →</Button>
    </form>,

    // Step 1: Address
    <div className="space-y-4">
      {/* State */}
      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700">State <span className="text-red-500">*</span></label>
        <select
          value={selectedState}
          onChange={(e) => { setSelectedState(e.target.value); setSelectedDistrict(''); setLocationId(null); }}
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 focus:outline-none"
        >
          <option value="">— Select State / UT —</option>
          {STATES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* District */}
      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700">District <span className="text-red-500">*</span></label>
        <select
          value={selectedDistrict}
          onChange={(e) => { setSelectedDistrict(e.target.value); setLocationId(null); }}
          disabled={!selectedState}
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
        >
          <option value="">— Select District —</option>
          {getDistricts(selectedState).map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        {!selectedState && (
          <p className="text-xs text-gray-400">Select state first</p>
        )}
      </div>

      <Input label="Village / Town / City" name="city_village" placeholder="Your village or town name"
        {...register('city_village')} />

      <Input label="Full Address" name="address_line" placeholder="House No., Street, Mohalla"
        {...register('address_line')} />

      <Input label="Pincode" name="pincode" inputMode="numeric" placeholder="6-digit pincode" maxLength={6}
        {...register('pincode')} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input label="Experience (years)" name="experience_years" type="number" min="0" max="50"
          {...register('experience_years')} />
        <Input label="Travel Radius (km)" name="travel_radius_km" type="number" min="0"
          hint="0 = local only" {...register('travel_radius_km')} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input label="Daily Wage Min (₹)" name="daily_wage_min" type="number" inputMode="numeric"
          {...register('daily_wage_min')} />
        <Input label="Daily Wage Max (₹)" name="daily_wage_max" type="number" inputMode="numeric"
          {...register('daily_wage_max')} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input label="Alternate Mobile" name="mobile_alt" type="tel" inputMode="numeric" placeholder="Optional"
          {...register('mobile_alt')} />
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Availability</label>
          <select className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none"
            {...register('availability_status')}>
            <option value="available">Available</option>
            <option value="unavailable">Not Available</option>
            <option value="on_job">Currently on a Job</option>
          </select>
        </div>
      </div>
      <div className="flex gap-3">
        <Button variant="secondary" onClick={() => profileExists ? navigate('/worker/dashboard') : setStep(0)}>← Back</Button>
        <Button fullWidth onClick={async () => {
          if (!selectedState || !selectedDistrict) {
            toast.error('Please select State and District');
            return;
          }
          try {
            let locId = locationId;
            if (!locId) {
              const locRes = await api.post('/lookup/location-resolve', {
                state: selectedState,
                district: selectedDistrict,
                city_village: getValues('city_village') || null,
                pincode: getValues('pincode') || null,
              });
              locId = locRes.data.data.id;
              setLocationId(locId);
            }
            await api.put('/worker/profile', { ...getValues(), location_id: locId });
            setStep(2);
          } catch {}
        }}>
          Save & Continue →
        </Button>
      </div>
    </div>,

    // Step 2: Skills
    <div className="space-y-4">
      <p className="text-sm text-gray-500">Select your skills (you can choose multiple)</p>
      <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto pr-1">
        {skills.map((s) => (
          <button key={s.id} type="button"
            onClick={() => toggleItem(selectedSkills, setSelectedSkills, s.id)}
            className={`text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${
              selectedSkills.includes(s.id)
                ? 'border-brand-500 bg-brand-50 text-brand-700'
                : 'border-gray-200 text-gray-700 hover:border-gray-300'
            }`}>
            {s.name}
            {s.category_name && <span className="block text-xs text-gray-400">{s.category_name}</span>}
          </button>
        ))}
      </div>
      <div className="flex gap-3">
        <Button variant="secondary" onClick={() => setStep(1)}>← Back</Button>
        <Button fullWidth onClick={saveSkills}>Save & Continue →</Button>
      </div>
    </div>,

    // Step 3: Preferences & Languages
    <div className="space-y-5">
      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700">Preferred Work Type</label>
        <select className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none"
          {...register('preferred_work_type')}>
          <option value="daily_wage">Daily Wage / दैनिक मजदूरी</option>
          <option value="contract">Contract / ठेका</option>
          <option value="seasonal">Seasonal / मौसमी</option>
          <option value="part_time">Part Time / अंशकालिक</option>
          <option value="full_time">Full Time / पूर्णकालिक</option>
        </select>
      </div>
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">Work Categories</p>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button key={c.id} type="button"
              onClick={() => toggleItem(selectedCategories, setSelectedCategories, c.id)}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                selectedCategories.includes(c.id) ? 'bg-brand-600 text-white border-brand-600' : 'border-gray-300 text-gray-600'
              }`}>
              {c.name}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">Languages Known</p>
        <div className="flex flex-wrap gap-2">
          {langs.map((l) => (
            <button key={l.id} type="button"
              onClick={() => toggleItem(selectedLangs, setSelectedLangs, l.id)}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                selectedLangs.includes(l.id) ? 'bg-india text-white border-india' : 'border-gray-300 text-gray-600'
              }`}>
              {l.name}
            </button>
          ))}
        </div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" className="rounded" {...register('open_to_relocation')} />
        <span className="text-sm text-gray-700">Open to relocation / स्थानांतरण के लिए तैयार</span>
      </label>
      <div className="flex gap-3">
        <Button variant="secondary" onClick={() => setStep(2)}>← Back</Button>
        <Button fullWidth onClick={savePreferences}>Save & Continue →</Button>
      </div>
    </div>,

    // Step 4: Documents & Payment
    <div className="space-y-5">
      <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
        <span className="text-3xl">🎉</span>
        <h3 className="font-bold text-green-800 mt-2">{profileExists ? 'Profile Updated!' : 'Profile Created!'}</h3>
        <p className="text-sm text-green-700 mt-1">{profileExists ? 'Your profile has been updated successfully.' : 'Pay ₹10 registration fee to activate your profile and start receiving job matches.'}</p>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
        <h4 className="font-medium text-gray-900">Next Steps</h4>
        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex items-center gap-2"><span className="text-green-500">✓</span> Profile created</div>
          <div className="flex items-center gap-2"><span className="text-yellow-500">○</span> Pay registration fee ₹10</div>
          <div className="flex items-center gap-2"><span className="text-gray-400">○</span> Upload Aadhaar card</div>
          <div className="flex items-center gap-2"><span className="text-gray-400">○</span> Profile verification (1-2 days)</div>
        </div>
      </div>
      {!profileExists && (
        <Button fullWidth onClick={() => navigate('/worker/payment/register')}>
          Pay Registration Fee ₹10
        </Button>
      )}
      <Button variant={profileExists ? 'primary' : 'secondary'} fullWidth onClick={() => navigate('/worker/dashboard')}>
        {profileExists ? 'Go to Dashboard' : 'Do it Later'}
      </Button>
    </div>,
  ];

  return (
    <WorkerLayout>
      <div className="max-w-lg mx-auto">
        <h1 className="text-xl font-bold text-gray-900 mb-6">{profileExists ? 'Update Your Profile' : 'Create Your Profile'}</h1>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2">
          {STEPS.map((s, i) => {
            if (profileExists && i === 0) return null;
            return (
              <div key={i} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 ${
                  i < step ? 'bg-brand-600 text-white' : i === step ? 'bg-brand-600 text-white ring-4 ring-brand-100' : 'bg-gray-200 text-gray-500'
                }`}>
                  {i < step ? '✓' : i + 1}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`h-0.5 w-8 sm:w-12 ${i < step ? 'bg-brand-600' : 'bg-gray-200'}`} />
                )}
              </div>
            );
          })}
        </div>
        <h2 className="text-base font-semibold text-gray-900 mb-5">{STEPS[step]}</h2>

        {stepContent[step]}
      </div>
    </WorkerLayout>
  );
}
